import { useState } from "react";
import { Plus, Trash2, Star, ChevronUp, ChevronDown } from "lucide-react";
import { adminUploadImage, resolveImageUrl } from "../lib/api";
import { toast } from "sonner";

export default function ProductImagesEditor({ images, onChange, testid = "product-images" }) {
  const list = images || [];
  const [busy, setBusy] = useState(false);

  const update = (next) => onChange(next);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const results = [];
      for (const f of files) {
        const { url } = await adminUploadImage(f);
        results.push(url);
      }
      update([...list, ...results]);
      toast.success(`${results.length} immagine${results.length > 1 ? " caricate" : " caricata"}`);
    } catch {
      toast.error("Upload fallito");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const setMain = (i) => {
    if (i === 0) return;
    const next = [...list];
    const [pick] = next.splice(i, 1);
    next.unshift(pick);
    update(next);
  };
  const remove = (i) => {
    if (!window.confirm("Rimuovere questa immagine?")) return;
    update(list.filter((_, idx) => idx !== i));
  };
  const move = (i, dir) => {
    const next = [...list];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    update(next);
  };
  const addUrl = () => {
    const url = window.prompt("URL dell'immagine:");
    if (!url) return;
    update([...list, url.trim()]);
  };

  return (
    <div data-testid={testid} className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <span className="label-eyebrow">Immagini del prodotto</span>
          <p className="font-body text-sm text-[color:var(--tr-ink-soft)] mt-1">
            La <strong>prima immagine</strong> è quella visibile nella galleria. Clicca la stella per sceglierla.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="tr-btn-ghost inline-flex items-center gap-2 cursor-pointer">
            <Plus className="w-4 h-4" /> Carica file
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={onUpload}
              className="hidden"
              disabled={busy}
              data-testid={`${testid}-upload`}
            />
          </label>
          <button
            type="button"
            onClick={addUrl}
            className="tr-btn-ghost inline-flex items-center gap-2"
          >
            URL
          </button>
        </div>
      </div>

      {list.length === 0 ? (
        <div className="border border-dashed border-[color:var(--tr-border)] p-8 text-center font-body text-[color:var(--tr-ink-soft)]">
          Nessuna immagine. Carica file o aggiungi un URL per iniziare.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {list.map((url, i) => (
            <div
              key={`${url}-${i}`}
              data-testid={`${testid}-item-${i}`}
              className={`relative border ${i === 0 ? "border-[color:var(--tr-ink)]" : "border-[color:var(--tr-border)]"} bg-[color:var(--tr-bg)]`}
            >
              <div className="aspect-[4/5] bg-[color:var(--tr-bg-2)]">
                <img
                  src={resolveImageUrl(url)}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </div>
              {i === 0 && (
                <span className="absolute top-2 left-2 bg-[color:var(--tr-ink)] text-[color:var(--tr-bg)] font-sub text-[0.6rem] uppercase tracking-[0.2em] px-2 py-1">
                  Principale
                </span>
              )}
              <div className="flex items-center justify-between px-2 py-2 border-t border-[color:var(--tr-border)]">
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setMain(i)}
                    disabled={i === 0}
                    title="Imposta come principale"
                    className="p-1.5 hover:bg-[color:var(--tr-bg-2)] disabled:opacity-30"
                  >
                    <Star className="w-3.5 h-3.5" strokeWidth={1.5} fill={i === 0 ? "currentColor" : "none"} />
                  </button>
                  <button
                    type="button"
                    onClick={() => move(i, -1)}
                    disabled={i === 0}
                    className="p-1.5 hover:bg-[color:var(--tr-bg-2)] disabled:opacity-30"
                    title="Sposta avanti"
                  >
                    <ChevronUp className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </button>
                  <button
                    type="button"
                    onClick={() => move(i, 1)}
                    disabled={i === list.length - 1}
                    className="p-1.5 hover:bg-[color:var(--tr-bg-2)] disabled:opacity-30"
                    title="Sposta indietro"
                  >
                    <ChevronDown className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  className="p-1.5 hover:bg-red-50 text-red-700"
                  title="Elimina"
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
